import { LitElement, html, css, nothing } from 'lit'
import { customElement, state } from 'lit/decorators.js'

export interface ToastAction {
  label: string
  callback: () => void
}

export interface ToastOptions {
  durationMs?: number | undefined
  action?: ToastAction | undefined
  tag?: string | undefined
}

export interface ToastMessage {
  id: number
  type: 'success' | 'error' | 'info'
  text: string
  action?: ToastAction | undefined
  tag?: string | undefined
}

let nextId = 0

@customElement('cairn-toast')
export class CairnToast extends LitElement {
  static override styles = css`
    :host {
      position: fixed;
      bottom: 80px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 10000;
      display: flex;
      flex-direction: column;
      gap: 8px;
      align-items: center;
      pointer-events: none;
      width: 90%;
      max-width: 400px;
    }

    .toast {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 12px 16px;
      border-radius: 8px;
      color: #fff;
      font-size: 14px;
      line-height: 1.4;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
      pointer-events: auto;
      opacity: 0;
      transform: translateY(20px);
      transition:
        opacity 0.3s,
        transform 0.3s;
      width: 100%;
      box-sizing: border-box;
      word-break: break-word;
    }

    .toast.show {
      opacity: 1;
      transform: translateY(0);
    }

    .toast.success {
      background: #16a34a;
    }

    .toast.error {
      background: #dc2626;
    }

    .toast.info {
      background: #2563eb;
    }

    .toast-text {
      flex: 1;
    }

    .close-btn {
      background: none;
      border: none;
      color: #fff;
      font-size: 18px;
      cursor: pointer;
      padding: 0 4px;
      opacity: 0.8;
      flex-shrink: 0;
    }

    .close-btn:hover,
    .close-btn:active {
      opacity: 1;
    }

    .action-btn {
      background: rgba(255, 255, 255, 0.2);
      border: 1px solid rgba(255, 255, 255, 0.4);
      border-radius: 4px;
      color: #fff;
      font-size: 13px;
      font-weight: 600;
      cursor: pointer;
      padding: 4px 10px;
      white-space: nowrap;
      flex-shrink: 0;
    }

    .action-btn:hover {
      background: rgba(255, 255, 255, 0.35);
    }
  `

  @state() private messages: ToastMessage[] = []

  show(type: ToastMessage['type'], text: string, options?: number | ToastOptions): void {
    const opts: ToastOptions =
      typeof options === 'number' ? { durationMs: options } : (options ?? {})
    const durationMs = opts.durationMs ?? 5000

    if (opts.tag) {
      this.dismissByTag(opts.tag)
    }

    const id = nextId++
    this.messages = [...this.messages, { id, type, text, action: opts.action, tag: opts.tag }]

    requestAnimationFrame(() => {
      const el = this.shadowRoot?.querySelector(`[data-id="${id}"]`)
      if (el) el.classList.add('show')
    })

    if (durationMs > 0) {
      setTimeout(() => this.dismiss(id), durationMs)
    }
  }

  private dismissByTag(tag: string): void {
    const toasts = this.messages.filter(m => m.tag === tag)
    for (const t of toasts) {
      this.dismiss(t.id)
    }
  }

  private dismiss(id: number): void {
    const el = this.shadowRoot?.querySelector(`[data-id="${id}"]`)
    if (el) {
      el.classList.remove('show')
      setTimeout(() => {
        this.messages = this.messages.filter(m => m.id !== id)
      }, 300)
    } else {
      this.messages = this.messages.filter(m => m.id !== id)
    }
  }

  override render() {
    return html`
      ${this.messages.map(
        m => html`
          <div class="toast ${m.type}" data-id="${m.id}">
            <span class="toast-text">${m.text}</span>
            ${m.action
              ? html`<button
                  class="action-btn"
                  @click=${() => {
                    m.action!.callback()
                    this.dismiss(m.id)
                  }}
                >
                  ${m.action.label}
                </button>`
              : nothing}
            <button class="close-btn" @click=${() => this.dismiss(m.id)}>&times;</button>
          </div>
        `
      )}
    `
  }
}

let instance: CairnToast | null = null

export function getToast(): CairnToast {
  if (!instance) {
    instance = document.querySelector('cairn-toast') as CairnToast
    if (!instance) {
      instance = document.createElement('cairn-toast') as CairnToast
      document.body.appendChild(instance)
    }
  }
  return instance
}

export function showToast(
  type: ToastMessage['type'],
  text: string,
  options?: number | ToastOptions
): void {
  getToast().show(type, text, options)
}
